import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useSettings } from './SettingsContext';
import { useWordDictionary } from '../hooks/useWordDictionary';
import * as wordUtils from '../utils/wordUtils';

const GameContext = createContext();

export const useGame = () => useContext(GameContext);

export const GameProvider = ({ children }) => {
  const { settings } = useSettings();
  const { 
    isLoading: isDictionaryLoading, 
    getWordsStartingWith, 
    isValidWord, 
    getRandomWord,
    getWordsThatCanFollow
  } = useWordDictionary();
  
  // Game state
  const [gameState, setGameState] = useState('setup'); // setup, playing, gameOver
  const [starter, setStarter] = useState('player'); // player or robot
  const [currentWord, setCurrentWord] = useState('');
  const [wordChain, setWordChain] = useState([]);
  const [score, setScore] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [playerInput, setPlayerInput] = useState('');
  const [isNgRule, setIsNgRule] = useState(false);
  const [timeLeft, setTimeLeft] = useState(settings.timeLimit);
  const [highScores, setHighScores] = useState(() => {
    const saved = localStorage.getItem('wordChainHighScores');
    return saved ? JSON.parse(saved) : [];
  });

  // Check if a word ends with ڭ (ng)
  const endsWithNg = useCallback((word) => {
    if (!word || word.length === 0) return false;
    return word[word.length - 1] === 'ڭ';
  }, []);

  // Effect to detect words ending with ڭ and set the special rule flag
  useEffect(() => {
    if (currentWord && endsWithNg(currentWord)) {
      setIsNgRule(true);
      setErrorMessage('سۆز "ڭ" بىلەن ئاخىرلاشتى! يېڭى قائىدە: خالىغان ھەرپ بىلەن باشلانغان سۆز كىرگۈزەلەيسىز.');
    }
  }, [currentWord, endsWithNg]);

  // Timer effect
  useEffect(() => {
    let timer;
    if (gameState === 'playing' && settings.timeLimit > 0) {
      timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            setGameState('gameOver');
            setErrorMessage('ۋاقىت توشتى!'); // Time's up!
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [gameState, settings.timeLimit]);

  // Save high scores
  useEffect(() => {
    localStorage.setItem('wordChainHighScores', JSON.stringify(highScores));
  }, [highScores]);

  // Start a new game
  const startGame = () => {
    setGameState('playing');
    setScore(0);
    setWordChain([]);
    setErrorMessage('');
    setIsNgRule(false);
    setTimeLeft(settings.timeLimit);
    
    if (starter === 'robot') {
      const robotWord = getRandomWord();
      setCurrentWord(robotWord);
      setWordChain([{ word: robotWord, player: 'robot' }]);
      
      // Check if robot's word ends with ڭ
      if (endsWithNg(robotWord)) {
        setIsNgRule(true);
        setErrorMessage('سۆز "ڭ" بىلەن ئاخىرلاشتى! يېڭى قائىدە: خالىغان ھەرپ بىلەن باشلانغان سۆز كىرگۈزەلەيسىز.');
      }
    } else {
      setCurrentWord('');
    }
  };

  // Get the last letter of a word
  const getLastLetter = (word) => {
    return wordUtils.getLastLetter(word);
  };

  // Handle player input submission
  const handleSubmit = () => {
    if (!playerInput.trim()) {
      setErrorMessage('سۆز كىرگۈزۈڭ!'); // Please enter a word
      return;
    }

    const input = playerInput.trim();
    setPlayerInput('');

    // If player starts the game
    if (wordChain.length === 0 && starter === 'player') {
      if (!isValidWord(input)) {
        setErrorMessage('بۇ سۆز بىزنىڭ لۇغىتىمىزدە يوق.'); // This word is not in our dictionary
        return;
      }
      
      setCurrentWord(input);
      setWordChain([{ word: input, player: 'player' }]);
      
      // Check if player's first word ends with ڭ
      if (endsWithNg(input)) {
        setIsNgRule(true);
        setErrorMessage('سۆز "ڭ" بىلەن ئاخىرلاشتى! يېڭى قائىدە: خالىغان ھەرپ بىلەن باشلانغان سۆز كىرگۈزەلەيسىز.');
      } else {
        // Robot's turn
        setTimeout(robotTurn, 1000, input);
      }
      return;
    }

    // Check if the word is already used
    if (wordChain.some(item => item.word === input)) {
      setGameState('gameOver');
      setErrorMessage('ئويۇن ئاخىرلاشتى! بۇ سۆز ئاللىقاچان ئىشلىتىلگەن.');
      addHighScore(score);
      return;
    }
    
    // Check if the word exists in dictionary
    if (!isValidWord(input)) {
      setGameState('gameOver');
      setErrorMessage('ئويۇن ئاخىرلاشتى! بۇ سۆز بىزنىڭ لۇغىتىمىزدە يوق.');
      addHighScore(score);
      return;
    }
    
    // Special rule for words ending with ڭ
    if (isNgRule) {
      console.log("NG rule is active - accepting any word");
      // When isNgRule is true, we accept any valid word regardless of first letter
      setCurrentWord(input);
      setWordChain([...wordChain, { word: input, player: 'player' }]);
      setScore(score + 1);
      setIsNgRule(false); // Turn off the special rule after use
      
      // Reset timer if there's a time limit
      if (settings.timeLimit > 0) {
        setTimeLeft(settings.timeLimit);
      }
      
      // Detect if the new word ends with ڭ
      if (endsWithNg(input)) {
        setIsNgRule(true);
        setErrorMessage('سۆز "ڭ" بىلەن ئاخىرلاشتى! يېڭى قائىدە: خالىغان ھەرپ بىلەن باشلانغان سۆز كىرگۈزەلەيسىز.');
      } else {
        // Robot's turn
        setTimeout(robotTurn, 1000, input);
      }
      return;
    }
    
    // Normal word chain rules for non-ڭ letters
    const lastLetter = getLastLetter(currentWord);
    if (!wordUtils.canWordFollow(currentWord, input)) {
      // Provide specific error message based on the expected first letter
      const effectiveLetter = wordUtils.getEffectiveLetterForMatching(lastLetter);
      const errorMsg = effectiveLetter === 'ئ' 
        ? `ئويۇن ئاخىرلاشتى! سۆز "ئ${lastLetter}" بىلەن باشلىنىشى كېرەك ئىدى.`
        : `ئويۇن ئاخىرلاشتى! سۆز "${lastLetter}" بىلەن باشلىنىشى كېرەك ئىدى.`;
      
      setGameState('gameOver');
      setErrorMessage(errorMsg);
      addHighScore(score);
      return;
    }

    // Word is valid
    setCurrentWord(input);
    setWordChain([...wordChain, { word: input, player: 'player' }]);
    setScore(score + 1);
    
    // Reset timer if there's a time limit
    if (settings.timeLimit > 0) {
      setTimeLeft(settings.timeLimit);
    }
    
    // Check if the new word ends with ڭ
    if (endsWithNg(input)) {
      setIsNgRule(true);
      setErrorMessage('سۆز "ڭ" بىلەن ئاخىرلاشتى! يېڭى قائىدە: خالىغان ھەرپ بىلەن باشلانغان سۆز كىرگۈزەلەيسىز.');
    } else {
      // Robot's turn
      setTimeout(robotTurn, 1000, input);
    }
  };

  // Robot's turn
  const robotTurn = (lastWord) => {
    // Special rule for ڭ - robot won't play after a word ending with ڭ
    if (endsWithNg(lastWord)) {
      // We've already set isNgRule to true in the effect
      return;
    }
    
    const lastLetter = getLastLetter(lastWord);
    
    // Get possible words that can follow this letter (handling Uyghur orthography)
    const possibleWords = getWordsThatCanFollow(lastLetter);
    
    // Filter out words that have already been used
    const unusedWords = possibleWords.filter(word => 
      !wordChain.some(item => item.word === word)
    );
    
    // Adjust difficulty
    let wordPool = unusedWords;
    if (settings.difficulty === 'easy') {
      // In easy mode, robot might choose shorter words that are easier to respond to
      wordPool = unusedWords.filter(word => word.length < 6);
      if (wordPool.length === 0) wordPool = unusedWords;
    } else if (settings.difficulty === 'hard') {
      // In hard mode, robot prefers words ending with difficult letters
      const hardLetters = wordUtils.getDifficultLetters();
      const hardWords = unusedWords.filter(word => 
        hardLetters.includes(wordUtils.getLastLetter(word))
      );
      if (hardWords.length > 0) {
        wordPool = hardWords;
      }
    }
    
    // If no words left, robot can't continue
    if (wordPool.length === 0) {
      if (unusedWords.length === 0) {
        setGameState('gameOver');
        setErrorMessage('ئويۇن ئاخىرلاشتى! روبوت داۋاملاشتۇرالمىدى.');
        addHighScore(score);
        return;
      }
      wordPool = unusedWords;
    }
    
    // Choose a random word
    const robotWord = wordPool[Math.floor(Math.random() * wordPool.length)];
    setCurrentWord(robotWord);
    setWordChain([...wordChain, { word: robotWord, player: 'robot' }]);
    
    // Check if robot's word ends with ڭ
    if (endsWithNg(robotWord)) {
      setIsNgRule(true);
      setErrorMessage('سۆز "ڭ" بىلەن ئاخىرلاشتى! يېڭى قائىدە: خالىغان ھەرپ بىلەن باشلانغان سۆز كىرگۈزەلەيسىز.');
    }
  };

  // Add score to high scores
  const addHighScore = (newScore) => {
    if (newScore > 0) {
      const newHighScore = {
        score: newScore,
        date: new Date().toISOString(),
        words: wordChain.length
      };
      
      setHighScores(prev => {
        const updated = [...prev, newHighScore].sort((a, b) => b.score - a.score).slice(0, 10);
        return updated;
      });
    }
  };

  return (
    <GameContext.Provider value={{
      gameState,
      setGameState,
      starter, 
      setStarter,
      currentWord,
      setCurrentWord,
      wordChain,
      setWordChain,
      score,
      setScore,
      errorMessage,
      setErrorMessage,
      playerInput,
      setPlayerInput,
      isNgRule,
      timeLeft,
      highScores,
      isDictionaryLoading,
      startGame,
      handleSubmit,
      getLastLetter
    }}>
      {children}
    </GameContext.Provider>
  );
};